import React, { useState, useEffect } from 'react';
import { ExternalLink, X } from 'lucide-react';
import api from '../api';

interface SourceConfig {
  name: string;
  shortName: string;
  logoUrl: string;
  connected: boolean;
  status: string;
  comingSoon: boolean;
  details?: Record<string, string | number>;
}

const LOGO_URLS: Record<string, string> = {
  'AWS': 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/Amazon_Web_Services_Logo.svg/512px-Amazon_Web_Services_Logo.svg.png',
  'CrowdStrike': 'https://www.crowdstrike.com/wp-content/uploads/2024/01/cs-logo-cslogo-red-mark.svg',
  'Okta': 'https://www.okta.com/sites/default/files/Okta_Logo_BrightBlue_Medium-thumbnail.png',
  'Azure': 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Microsoft_Azure.svg/150px-Microsoft_Azure.svg.png',
  'Checkpoint': 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Check_Point_logo_2022.svg/512px-Check_Point_logo_2022.svg.png',
  'Fortinet': 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/62/Fortinet_logo.svg/512px-Fortinet_logo.svg.png',
  'GCP': 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/51/Google_Cloud_logo.svg/512px-Google_Cloud_logo.svg.png',
  'Jira': 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8a/Jira_Logo.svg/512px-Jira_Logo.svg.png',
  'ServiceNow': 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/57/ServiceNow_logo.svg/512px-ServiceNow_logo.svg.png',
  'Zscaler': 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/Zscaler_logo.svg/512px-Zscaler_logo.svg.png',
};

const ConnectedSources: React.FC = () => {
  const [sources, setSources] = useState<SourceConfig[]>([
    { name: 'AWS Security', shortName: 'AWS', logoUrl: LOGO_URLS['AWS'], connected: false, status: 'Checking...', comingSoon: false },
    { name: 'CrowdStrike Falcon', shortName: 'CrowdStrike', logoUrl: LOGO_URLS['CrowdStrike'], connected: false, status: 'Checking...', comingSoon: false },
    { name: 'Okta', shortName: 'Okta', logoUrl: LOGO_URLS['Okta'], connected: false, status: 'Checking...', comingSoon: false },
    { name: 'Microsoft Azure', shortName: 'Azure', logoUrl: LOGO_URLS['Azure'], connected: false, status: 'Coming Soon', comingSoon: true },
    { name: 'Check Point', shortName: 'Checkpoint', logoUrl: LOGO_URLS['Checkpoint'], connected: false, status: 'Coming Soon', comingSoon: true },
    { name: 'Fortinet', shortName: 'Fortinet', logoUrl: LOGO_URLS['Fortinet'], connected: false, status: 'Coming Soon', comingSoon: true },
    { name: 'Google Cloud', shortName: 'GCP', logoUrl: LOGO_URLS['GCP'], connected: false, status: 'Coming Soon', comingSoon: true },
    { name: 'Jira', shortName: 'Jira', logoUrl: LOGO_URLS['Jira'], connected: false, status: 'Coming Soon', comingSoon: true },
    { name: 'ServiceNow', shortName: 'ServiceNow', logoUrl: LOGO_URLS['ServiceNow'], connected: false, status: 'Coming Soon', comingSoon: true },
    { name: 'Zscaler', shortName: 'Zscaler', logoUrl: LOGO_URLS['Zscaler'], connected: false, status: 'Coming Soon', comingSoon: true },
  ]);

  const [expandedSource, setExpandedSource] = useState<string | null>(null);
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    const checkConnections = async () => {
      try {
        const findings = await api.aws.getFindings();
        setSources(prev => prev.map(s => s.shortName === 'AWS' ? {
          ...s, connected: true, status: 'Connected',
          details: { 'Data Sources': 'GuardDuty, Security Hub', 'Real-Time Ingestion': 'Enabled', 'Alerts Monitored': findings.length || 0, 'Events/Hour': '2.4K', 'Last Sync': 'Just now' }
        } : s));
      } catch (e) {
        setSources(prev => prev.map(s => s.shortName === 'AWS' ? { ...s, connected: false, status: 'Error' } : s));
      }

      try {
        const hosts = await api.crowdstrike.getHosts();
        setSources(prev => prev.map(s => s.shortName === 'CrowdStrike' ? {
          ...s, connected: true, status: hosts.length > 0 ? 'Connected' : 'No Hosts',
          details: { 'Hosts Monitored': hosts.length || 0, 'Detection Mode': 'Real-time', 'Last Check': 'Just now' }
        } : s));
      } catch (e) {
        setSources(prev => prev.map(s => s.shortName === 'CrowdStrike' ? { ...s, connected: true, status: 'API Connected', details: { 'Note': 'No hosts enrolled yet' } } : s));
      }

      try {
        const users = await api.okta.getUsers();
        setSources(prev => prev.map(s => s.shortName === 'Okta' ? {
          ...s, connected: true, status: 'Connected',
          details: { 'Users Monitored': users.length || 0, 'Log Streaming': 'Enabled', 'Last Sync': 'Just now' }
        } : s));
      } catch (e) {
        setSources(prev => prev.map(s => s.shortName === 'Okta' ? { ...s, connected: false, status: 'Error' } : s));
      }
    };

    checkConnections();
    const interval = setInterval(checkConnections, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleImgError = (name: string) => {
    setImgErrors(prev => new Set(prev).add(name));
  };

  return (
    <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Connected Sources</h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            <span className="text-green-400 font-medium">{sources.filter(s => s.connected).length}</span> / {sources.length} Active
          </span>
          <ExternalLink className="w-4 h-4 text-gray-500" />
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
        {sources.map((source) => (
          <div
            key={source.name}
            onClick={() => {
              if (!source.comingSoon && source.connected) {
                setExpandedSource(expandedSource === source.name ? null : source.name);
              }
            }}
            className={`relative flex-shrink-0 rounded-xl border-2 transition-all duration-200 ${
              source.comingSoon 
                ? 'border-gray-700/30 opacity-50 cursor-default' 
                : expandedSource === source.name
                  ? 'border-arbitium-accent bg-arbitium-accent/10 cursor-pointer'
                  : source.connected 
                    ? 'border-green-500/40 hover:border-green-400 hover:bg-green-500/5 cursor-pointer' 
                    : 'border-red-500/40 hover:border-red-400 cursor-pointer'
            }`}
            style={{ width: '120px', minWidth: '120px' }}
          >
            <div className="flex flex-col items-center p-4 gap-2">
              {/* Logo - ALL have white background for visibility */}
              <div className="w-12 h-12 rounded-lg flex items-center justify-center p-2 bg-white shadow-sm">
                {imgErrors.has(source.name) ? (
                  <span className="text-xl font-bold text-gray-600">{source.shortName.charAt(0)}</span>
                ) : (
                  <img
                    src={source.logoUrl}
                    alt={source.name}
                    className={`w-full h-full object-contain ${source.comingSoon ? 'opacity-60' : ''}`}
                    onError={() => handleImgError(source.name)}
                    loading="lazy"
                  />
                )}
              </div>
              
              {/* Name */}
              <span className={`text-xs font-semibold text-center leading-tight ${source.comingSoon ? 'text-gray-500' : 'text-white'}`}>
                {source.shortName}
              </span>
              
              {/* Status */}
              <div className="flex items-center gap-1.5">
                {source.comingSoon ? (
                  <span className="text-[10px] text-gray-500 font-medium">Coming Soon</span>
                ) : source.connected ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    <span className="text-[10px] text-green-400 font-semibold">Live</span>
                  </>
                ) : (
                  <>
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    <span className="text-[10px] text-red-400 font-semibold">Error</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Expanded details modal */}
      {expandedSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setExpandedSource(null)}>
          <div className="bg-arbitium-dark border-2 border-arbitium-accent/50 rounded-2xl shadow-2xl shadow-arbitium-accent/20 p-6 w-80" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const source = sources.find(s => s.name === expandedSource);
              if (!source) return null;
              return (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white p-1.5 shadow-sm">
                        <img src={source.logoUrl} alt={source.name} className="w-full h-full object-contain" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-white">{source.name}</p>
                        <div className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                          <span className="text-xs text-green-400">Connected</span>
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={() => setExpandedSource(null)}
                      className="p-2 hover:bg-arbitium-primary/20 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                  
                  {source.details && (
                    <div className="space-y-3 bg-arbitium-darker/50 rounded-xl p-4">
                      {Object.entries(source.details).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-gray-400">{key}</span>
                          <span className="text-white font-mono font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-arbitium-primary/20 flex items-center justify-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    <span className="text-xs text-green-400 font-medium">Connection Healthy</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectedSources;
